/**
 * BookingsCalendar — a property-row timeline (Gantt-style) for the Bookings
 * register. Properties are rows, days are columns (a 14-day window), and each
 * booking renders as a single horizontal bar spanning its check-in → check-out
 * days (clamped to the visible window), colored by channel and labelled with the
 * guest name + channel.
 *
 * Fully dynamic: it plots whatever {@link BookingRow}s it is given (already
 * filtered by the screen's filter bar). Cancelled bookings are excluded by the
 * caller (they no longer hold inventory). Clicking a bar invokes `onSelect` so
 * the screen can open the same detail drawer as the table.
 *
 * Layout uses CSS grid: a fixed label column + N equal day columns. A booking
 * bar is absolutely positioned within its property row across the day columns
 * it covers, so multi-day stays are one continuous bar. SSR-safe: pure render
 * from props (the caller owns the window Date math in effects/handlers).
 */
import type { ReactElement } from "react";
import type { BookingRow } from "./services.js";

/** Channel → bar colour (brand-derived; stable per channel). */
const CHANNEL_COLORS: Record<string, string> = {
  Direct: "#1D35A9", // Primary blue
  "Booking.com": "#0067B1", // Deep blue
  Airbnb: "#B42318", // Red
  Agoda: "#F79009", // Amber
  Expedia: "#667085", // Slate gray
  MakeMyTrip: "#12B76A", // Green
  Goibibo: "#00558F", // Darkest blue
};
/** Fallback palette for unknown channels (stable by hash). */
const FALLBACK_COLORS = ["#1D35A9", "#12B76A", "#F79009", "#667085", "#B42318"];

/** Resolve a channel to a bar colour. */
function channelColor(channel: string): string {
  const known = CHANNEL_COLORS[channel];
  if (known !== undefined) return known;
  let h = 0;
  for (let i = 0; i < channel.length; i += 1) {
    h = (h * 31 + channel.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length] as string;
}

/** A day cell descriptor for the header. */
export interface CalendarDay {
  /** ISO `YYYY-MM-DD`. */
  readonly iso: string;
  /** Day-of-month number. */
  readonly dayNum: number;
  /** Single-letter weekday (S M T W T F S). */
  readonly weekday: string;
  /** True for today. */
  readonly isToday: boolean;
  /** True for Sat/Sun. */
  readonly isWeekend: boolean;
}

/** One property row with its bookings. */
interface PropertyRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly bookings: readonly BookingRow[];
}

/** Days difference (b - a) for two ISO `YYYY-MM-DD` dates (UTC-safe). */
function daysBetween(aIso: string, bIso: string): number {
  const a = Date.parse(`${aIso}T00:00:00Z`);
  const b = Date.parse(`${bIso}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** Group bookings by property, preserving a stable name order. */
function groupByProperty(rows: readonly BookingRow[]): PropertyRow[] {
  const map = new Map<string, PropertyRow>();
  for (const b of rows) {
    const key = b.propertyId ?? "unassigned";
    const name = b.propertyName ?? "Unassigned";
    const existing = map.get(key);
    if (existing === undefined) {
      map.set(key, { propertyId: key, propertyName: name, bookings: [b] });
    } else {
      (existing.bookings as BookingRow[]).push(b);
    }
  }
  return [...map.values()].sort((x, y) =>
    x.propertyName.localeCompare(y.propertyName),
  );
}

/** Props for {@link BookingsCalendar}. */
export interface BookingsCalendarProps {
  /** The visible day cells (already computed by the screen). */
  readonly days: readonly CalendarDay[];
  /** The bookings to plot (already filtered; cancelled excluded). */
  readonly rows: readonly BookingRow[];
  /** Open the detail drawer for a booking. */
  readonly onSelect: (booking: BookingRow) => void;
}

export function BookingsCalendar({
  days,
  rows,
  onSelect,
}: BookingsCalendarProps): ReactElement {
  const properties = groupByProperty(rows);
  const windowStart = days[0]?.iso ?? "";
  const windowLen = days.length;
  // Grid template: a fixed label column + N equal day columns.
  const gridTemplate = `minmax(9rem, 12rem) repeat(${windowLen}, minmax(2.6rem, 1fr))`;

  return (
    <div className="bkcal" role="grid" aria-label="Bookings calendar">
      {/* Header row: blank label cell + day columns. */}
      <div
        className="bkcal__header"
        style={{ gridTemplateColumns: gridTemplate }}
        role="row"
      >
        <div className="bkcal__corner" role="columnheader">
          Property
        </div>
        {days.map((d) => (
          <div
            key={d.iso}
            className={
              "bkcal__day" +
              (d.isToday ? " bkcal__day--today" : "") +
              (d.isWeekend ? " bkcal__day--weekend" : "")
            }
            role="columnheader"
          >
            <span className="bkcal__daynum">{d.dayNum}</span>
            <span className="bkcal__weekday">{d.weekday}</span>
          </div>
        ))}
      </div>

      {/* One row per property. */}
      {properties.length === 0 ? (
        <p className="console-empty">No bookings in this window.</p>
      ) : (
        properties.map((prop) => (
          <div
            key={prop.propertyId}
            className="bkcal__row"
            style={{ gridTemplateColumns: gridTemplate }}
            role="row"
          >
            <div className="bkcal__label" role="rowheader">
              {prop.propertyName}
            </div>
            {/* Background day cells (for gridlines / weekend shading). */}
            <div className="bkcal__track" style={{ gridColumn: `2 / span ${windowLen}` }}>
              <div
                className="bkcal__cells"
                style={{ gridTemplateColumns: `repeat(${windowLen}, minmax(2.6rem, 1fr))` }}
              >
                {days.map((d) => (
                  <span
                    key={d.iso}
                    className={
                      "bkcal__cell" +
                      (d.isToday ? " bkcal__cell--today" : "") +
                      (d.isWeekend ? " bkcal__cell--weekend" : "")
                    }
                  />
                ))}
              </div>
              {/* Booking bars overlaid on the track. */}
              {prop.bookings.map((b) => {
                // Clamp the booking span to the visible window.
                const startOffset = Math.max(0, daysBetween(windowStart, b.checkIn));
                const endOffset = Math.min(
                  windowLen,
                  daysBetween(windowStart, b.checkOut),
                );
                const span = endOffset - startOffset;
                if (span <= 0 || startOffset >= windowLen) {
                  return null; // outside the window
                }
                const leftPct = (startOffset / windowLen) * 100;
                const widthPct = (span / windowLen) * 100;
                const color = channelColor(b.channel);
                const clippedStart = daysBetween(windowStart, b.checkIn) < 0;
                const clippedEnd =
                  daysBetween(windowStart, b.checkOut) > windowLen;
                return (
                  <button
                    key={b.id}
                    type="button"
                    className={
                      "bkcal__bar" +
                      (b.status === "held" ? " bkcal__bar--held" : "") +
                      (clippedStart ? " bkcal__bar--clip-start" : "") +
                      (clippedEnd ? " bkcal__bar--clip-end" : "")
                    }
                    style={{
                      left: `${leftPct}%`,
                      width: `calc(${widthPct}% - 4px)`,
                      background: color,
                    }}
                    title={`${b.guestName ?? "Guest"} · ${b.channel} · ${b.checkIn} → ${b.checkOut}`}
                    onClick={() => onSelect(b)}
                  >
                    <span className="bkcal__bar-guest">
                      {b.guestName ?? "Guest"}
                    </span>
                    <span className="bkcal__bar-channel"> · {b.channel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default BookingsCalendar;
